import * as React from "react";
import { themeColors, themeTextColors } from "@common/constants";
import { ProductTopic, ProductPhase } from "@modules/tasks/types";
import { ICONS_BY_PLAYBOOK_DOMAIN } from "@modules/tasks/constants";
import HoverTooltip from "@common/components/HoverTooltip";

interface WealthPlaybookDomainDisplayProps {
  progress?: number;
  playbookDomain: ProductTopic | ProductPhase.PHASE_1;
}

const WealthPlaybookDomainDisplay: React.FC<
  WealthPlaybookDomainDisplayProps
> = ({ playbookDomain, progress = 0 }) => {
  const [offset, setOffset] = React.useState(0);
  const CIRCLE_STROKE_WIDTH = 5;
  const CIRCLE_RADIUS = (70 - CIRCLE_STROKE_WIDTH) / 2;
  const CIRCLE_RADIUS_WITH_BORDER = CIRCLE_RADIUS + CIRCLE_STROKE_WIDTH / 2;
  const CIRCLE_STROKE_LENGTH = 2 * Math.PI * CIRCLE_RADIUS;
  React.useEffect(() => {
    const progressOffset = ((100 - progress) / 100) * CIRCLE_STROKE_LENGTH;
    setOffset(progressOffset);
  }, [CIRCLE_STROKE_LENGTH, progress]);

  const LABELS_BY_PLAYBOOK_DOMAIN = {
    [ProductPhase.PHASE_1]: "Onboarding",
    [ProductTopic.FINANCIAL_PLAN]: "Financial Plan",
    [ProductTopic.INVESTMENT_PLAN]: "Investments",
    [ProductTopic.TAX_PLAN]: "Tax Planning",
    [ProductTopic.TAX_CENTER]: "Tax Center",
    [ProductTopic.CAREER]: "Career Advisory",
    [ProductTopic.INSURANCE_PLAN]: "Insurance",
    [ProductTopic.DEBT_AND_CASH]: "Debt & Cash Management",
    [ProductTopic.ESTATE_PLAN]: "Trust & Estate",
  };

  const sharedCircleProps = {
    strokeLinecap: "round",
    strokeWidth: CIRCLE_STROKE_WIDTH,
    vectorEffect: "non-scaling-stroke",
    shapeRendering: "auto",
    r: CIRCLE_RADIUS,
    cx: CIRCLE_RADIUS_WITH_BORDER,
    cy: CIRCLE_RADIUS_WITH_BORDER,
  } as const;
  const IconComponent = ICONS_BY_PLAYBOOK_DOMAIN[playbookDomain];

  return (
    <HoverTooltip title={LABELS_BY_PLAYBOOK_DOMAIN[playbookDomain]}>
      <div
        className="relative h-[70px] w-[70px]"
        data-testid="wealth-playbook-domain-mini-display"
      >
        <svg className="absolute inset-0 h-full w-full">
          <circle
            {...sharedCircleProps}
            stroke={themeTextColors["primary"]}
            fill={progress > 0 ? themeColors.green[2] : themeColors.green[4]}
          />
          <circle
            {...sharedCircleProps}
            stroke={
              progress > 0 ? themeColors.data.green : themeColors.green[5]
            }
            fill="transparent"
            strokeDasharray={CIRCLE_STROKE_LENGTH}
            strokeDashoffset={progress > 0 ? offset : 0}
            style={{
              transition: "stroke-dashoffset 0.3s ease-out",
            }}
            transform={`rotate(-90 ${CIRCLE_RADIUS_WITH_BORDER} ${CIRCLE_RADIUS_WITH_BORDER})`}
          />
        </svg>
        <div className="absolute inset-0 m-auto flex flex-col items-center justify-center">
          <IconComponent
            width={35} // Width of the image
            height={35} // Height of the image
            color="white"
          />
        </div>
      </div>
    </HoverTooltip>
  );
};

export default WealthPlaybookDomainDisplay;
